import React, { ReactChild } from 'react'

type Regressors = {
  edad: number | string
  genero: string
  nacionalidad: string
  pais_actual: string
}

export type AppContextData = {
  datetime: string
  visited: Array<string>
  experiment: Experiment
  regressors?: Regressors
}

export type Experiment = {
  paso1?: Record<string, string>
  paso2?: Record<string, string>
  paso3?: Record<string, string>
  paso4?: Record<string, string>
  calculosOcean?: {
    extraversion: string | number
    agreeableness: string | number
    conscientiousness: string | number
    neuroticism: string | number
    openness: string | number
    percentil_extraversion: string | number
    percentil_agreeableness: string | number
    percentil_conscientiousness: string | number
    percentil_neuroticism: string | number
    percentil_openness: string | number
    extraversion_percentil_mostrado: string | number
    agreeableness_percentil_mostrado: string | number
    conscientiousness_percentil_mostrado: string | number
    neuroticism_percentil_mostrado: string | number
    openness_percentil_mostrado: string | number
  }
}

const initialAppContextData: () => AppContextData = () => {
  return {
    datetime: new Date().toLocaleString('es', { hour12: false }),
    visited: [],
    experiment: {},
  }
}

type Visit = {
  type: 'REGISTER_VISIT'
  route: string
}

type AcceptTerms = {
  type: 'ACCEPT_TERMS'
}

type SaveRegressors = {
  type: 'SAVE_REGRESSORS'
  data: Regressors
}

type SubmitStep = {
  type: 'SUBMIT_STEP'
  step: 1 | 2 | 3 | 4
  values: Record<string, string>
}

type SubmitCalculo = {
  type: 'SUBMIT_CALCULO'
  values: Experiment['calculosOcean']
}

type ReducerAction =
  | AcceptTerms
  | Visit
  | SubmitStep
  | SubmitCalculo
  | SaveRegressors

const mutateExperimentState = (state: AppContextData) => <
  T extends keyof Experiment
>(
  key: T
) => (data: Experiment[T]) => ({
  ...state,
  experiment: {
    ...state.experiment,
    [key]: data,
  },
})

function reducer(state: AppContextData, action: ReducerAction): AppContextData {
  switch (action.type) {
    case 'REGISTER_VISIT': {
      return {
        ...state,
        visited: [...state.visited, action.route],
      }
    }
    case 'SAVE_REGRESSORS': {
      const { data } = action
      return {
        ...state,
        regressors: data,
      }
    }
    case 'ACCEPT_TERMS': {
      return {
        ...state,
      }
    }
    case 'SUBMIT_STEP': {
      return mutateExperimentState(state)(
        ('paso' + action.step) as keyof Experiment
      )(action.values)
    }
    case 'SUBMIT_CALCULO': {
      return mutateExperimentState(state)('calculosOcean')(action.values)
    }
  }
}

export const AppContext = React.createContext({
  appState: initialAppContextData(),
  dispatch: (value: ReducerAction) => {},
})

export function AppContextProvider(props: { children: ReactChild }) {
  const [state, dispatch] = React.useReducer(reducer, initialAppContextData())

  return (
    <AppContext.Provider value={{ appState: state, dispatch }}>
      {props.children}
    </AppContext.Provider>
  )
}
